import { Ionicons } from '@expo/vector-icons';
import {
  View,
  Text,
  ScrollView,
  RefreshControl,
  Alert,
  Pressable,
  Platform,
} from 'react-native';
import { useState, useCallback } from 'react';
import { useIsFocused, useRouter } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import { trpc } from '../../lib/trpc';
import {
  AccountTaxSubtypeReviewSheet,
  AssetForm,
  Card,
  SectionHeader,
  LoadingSpinner,
  EmptyState,
  ErrorState,
  Badge,
  Button,
  ConnectionErrorBanner,
  CurrencyInput,
  InstitutionLogo,
  Money,
  PageContainer,
  PageHeader,
  useConfirmationDialog,
  ReviewHintIcon,
  TextInput,
  WebHead,
} from '../../components';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { useColors } from '../../lib/colors';
import { formatCurrency, formatCurrencyChange, formatPercent, formatRelativeTime, toTitleCase } from '../../lib/utils';
import { getAccountTaxSubtypeLabel } from '../../lib/accountTaxSubtypes';
import { useScreenReadyTelemetry } from '../../hooks/useScreenReadyTelemetry';
import { buildBalanceSheetSnapshot } from '../../lib/balance-sheet-statement';

type AccountType = 'depository' | 'investment' | 'credit' | 'loan' | 'other';
type AssetType = 'home' | 'vehicle' | 'rental_property' | 'other';

const TYPE_LABELS: Record<AccountType, string> = {
  depository: 'Cash & Checking',
  investment: 'Investment Accounts',
  credit: 'Credit Cards',
  loan: 'Loans',
  other: 'Other Accounts',
};

function sortByBalanceDesc(
  a: { balance: number | string | null },
  b: { balance: number | string | null },
): number {
  return Number(b.balance) - Number(a.balance);
}

function getAssetIconName(assetType: AssetType): React.ComponentProps<typeof Ionicons>['name'] {
  switch (assetType) {
    case 'home':
      return 'home';
    case 'vehicle':
      return 'car-sport';
    case 'rental_property':
      return 'business';
    default:
      return 'cube';
  }
}

function getAssetIconColors(assetType: AssetType) {
  switch (assetType) {
    case 'home':
      return { bg: 'bg-sky-50', fg: '#0284c7' };
    case 'vehicle':
      return { bg: 'bg-amber-50', fg: '#d97706' };
    case 'rental_property':
      return { bg: 'bg-emerald-50', fg: '#059669' };
    default:
      return { bg: 'bg-stone-100', fg: '#57534e' };
  }
}

function AccountPlanningButton({
  onPress,
}: {
  onPress: () => void;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const isWeb = Platform.OS === 'web';
  const tooltip = 'Review account planning treatment';

  return (
    <View className="relative">
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={tooltip}
        accessibilityHint="Opens account tax label and FIRE inclusion settings"
        hitSlop={8}
        onHoverIn={isWeb ? () => setShowTooltip(true) : undefined}
        onHoverOut={isWeb ? () => setShowTooltip(false) : undefined}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        onPress={(event) => {
          event.stopPropagation?.();
          setShowTooltip(false);
          onPress();
        }}
        className="rounded-full p-1 active:opacity-70 web:cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        {({ pressed }) => (
          <Ionicons
            name="options-outline"
            size={16}
            color={showTooltip || pressed ? '#374151' : '#9ca3af'}
          />
        )}
      </Pressable>

      {isWeb && showTooltip ? (
        <View className="absolute right-0 bottom-7 z-50 w-44 rounded-md border border-gray-200 bg-white px-2 py-1 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <Text className="text-[11px] leading-4 text-gray-700 dark:text-gray-200">
            {tooltip}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

function formatAssetMeta(asset: {
  address: {
    street: string | null;
    city: string | null;
    region: string | null;
    postalCode: string | null;
  } | null;
  vehicle: {
    year: number | null;
    make: string | null;
    model: string | null;
    trim: string | null;
    mileage: number | null;
  } | null;
  description: string | null;
}) {
  if (asset.vehicle) {
    const vehicleLabel = [
      asset.vehicle.year,
      asset.vehicle.make,
      asset.vehicle.model,
      asset.vehicle.trim,
    ]
      .filter(Boolean)
      .join(' ');

    if (asset.vehicle.mileage != null) {
      return `${vehicleLabel} · ${asset.vehicle.mileage.toLocaleString()} miles`;
    }

    if (vehicleLabel) return vehicleLabel;
  }

  if (asset.address) {
    const addressLabel = [
      asset.address.street,
      asset.address.city,
      asset.address.region,
      asset.address.postalCode,
    ]
      .filter(Boolean)
      .join(', ');

    if (addressLabel) return addressLabel;
  }

  return asset.description ?? 'Major asset';
}

export default function AccountsScreen() {
  const router = useRouter();
  const isFocused = useIsFocused();
  const isDesktop = useIsDesktop();
  const c = useColors();
  const [refreshing, setRefreshing] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [reviewSheetAccountIds, setReviewSheetAccountIds] = useState<string[]>([]);
  const [showZeroBalanceInvestments, setShowZeroBalanceInvestments] = useState(false);
  const [editingLiabilityId, setEditingLiabilityId] = useState<string | null>(null);
  const [editingManualBalanceId, setEditingManualBalanceId] = useState<string | null>(null);
  const [manualBalanceDraft, setManualBalanceDraft] = useState('');
  const [addingAsset, setAddingAsset] = useState(false);
  const [liabilityDraft, setLiabilityDraft] = useState<{
    principalAndInterestMonthly: string;
    propertyTaxMonthly: string;
    homeInsuranceMonthly: string;
    hoaMonthly: string;
  } | null>(null);

  const householdQ = trpc.households.get.useQuery(undefined, { enabled: isFocused });
  const accountsQ = trpc.accounts.list.useQuery(undefined, { enabled: isFocused });
  const assetsQ = trpc.assets.list.useQuery(undefined, { enabled: isFocused });
  const liabilitiesQ = trpc.liabilities.list.useQuery(undefined, { enabled: isFocused });
  const taxEstimateQ = trpc.tax.currentYearEstimate.useQuery(undefined, { enabled: isFocused });
  const holdingsQ = trpc.holdings.list.useQuery(undefined, { enabled: isFocused });
  const connectionsQ = trpc.connections.list.useQuery(undefined, {
    enabled: isFocused,
    refetchInterval: (query) => {
      const isSyncing = query.state.data?.some((c) => c.status === 'syncing');
      return isSyncing ? 2000 : false;
    },
  });
  useScreenReadyTelemetry({
    ready: accountsQ.data != null && assetsQ.data != null && liabilitiesQ.data != null,
    screenName: 'accounts',
  });
  const createAssetMutation = trpc.assets.create.useMutation();
  const deleteAssetMutation = trpc.assets.delete.useMutation();
  const estimateValueMutation = trpc.assets.estimateValue.useMutation();
  const updateLiabilityPlanningMutation = trpc.liabilities.upsertPlanning.useMutation();
  const updateAccountMutation = trpc.accounts.update.useMutation();
  const utils = trpc.useUtils();

  const refetchAll = useCallback(async () => {
    setRefreshing(true);
    await Promise.allSettled([
      accountsQ.refetch(),
      assetsQ.refetch(),
      liabilitiesQ.refetch(),
      connectionsQ.refetch(),
    ]);
    setRefreshing(false);
  }, [accountsQ, assetsQ, liabilitiesQ, connectionsQ]);

  const { confirmDestructive } = useConfirmationDialog();

  const startEditingManualBalance = useCallback(
    (account: { id: string; balance: number | string | null }) => {
      setEditingManualBalanceId(account.id);
      setManualBalanceDraft(account.balance != null ? String(account.balance) : '');
    },
    [],
  );

  const cancelEditingManualBalance = useCallback(() => {
    setEditingManualBalanceId(null);
    setManualBalanceDraft('');
  }, []);

  const handleSaveManualBalance = useCallback(async () => {
    if (!editingManualBalanceId) return;
    const parsed = Number(manualBalanceDraft);
    if (!Number.isFinite(parsed)) {
      Alert.alert('Invalid balance', 'Enter a valid number.');
      return;
    }
    try {
      await updateAccountMutation.mutateAsync({
        id: editingManualBalanceId,
        balance: parsed,
      });
      await Promise.allSettled([
        accountsQ.refetch(),
        utils.accounts.getMetrics.invalidate(),
        utils.accounts.getBalancesByTaxType.invalidate(),
        utils.fire.getPlan.invalidate(),
      ]);
      cancelEditingManualBalance();
    } catch {
      Alert.alert('Error', 'Could not update the balance.');
    }
  }, [
    accountsQ,
    cancelEditingManualBalance,
    editingManualBalanceId,
    manualBalanceDraft,
    updateAccountMutation,
    utils.accounts.getBalancesByTaxType,
    utils.accounts.getMetrics,
    utils.fire.getPlan,
  ]);

  const startEditingLiability = useCallback((liability: NonNullable<typeof liabilitiesQ.data>[number]) => {
    setEditingLiabilityId(liability.id);
    setLiabilityDraft({
      principalAndInterestMonthly:
        liability.principalAndInterestMonthly != null
          ? String(liability.principalAndInterestMonthly)
          : '',
      propertyTaxMonthly:
        liability.propertyTaxMonthly != null ? String(liability.propertyTaxMonthly) : '',
      homeInsuranceMonthly:
        liability.homeInsuranceMonthly != null
          ? String(liability.homeInsuranceMonthly)
          : '',
      hoaMonthly: liability.hoaMonthly != null ? String(liability.hoaMonthly) : '',
    });
  }, [liabilitiesQ.data]);

  const cancelEditingLiability = useCallback(() => {
    setEditingLiabilityId(null);
    setLiabilityDraft(null);
  }, []);

  const parseOptionalNumber = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const handleSaveLiabilityPlanning = useCallback(async () => {
    if (!editingLiabilityId || !liabilityDraft) return;
    try {
      await updateLiabilityPlanningMutation.mutateAsync({
        liabilityId: editingLiabilityId,
        principalAndInterestMonthly: parseOptionalNumber(liabilityDraft.principalAndInterestMonthly),
        propertyTaxMonthly: parseOptionalNumber(liabilityDraft.propertyTaxMonthly),
        homeInsuranceMonthly: parseOptionalNumber(liabilityDraft.homeInsuranceMonthly),
        hoaMonthly: parseOptionalNumber(liabilityDraft.hoaMonthly),
      });
      await Promise.allSettled([
        liabilitiesQ.refetch(),
        utils.fire.getPlan.invalidate(),
        utils.fire.getRecalcStatus.invalidate(),
        utils.budget.getEntries.invalidate(),
      ]);
      cancelEditingLiability();
    } catch {
      Alert.alert('Error', 'Could not update the mortgage planning breakdown.');
    }
  }, [
    cancelEditingLiability,
    editingLiabilityId,
    liabilityDraft,
    liabilitiesQ,
    updateLiabilityPlanningMutation,
    utils.budget.getEntries,
    utils.fire.getPlan,
    utils.fire.getRecalcStatus,
  ]);

  if (
    accountsQ.isLoading
    && assetsQ.isLoading
    && liabilitiesQ.isLoading
  ) {
    return <LoadingSpinner />;
  }

  const queryError = accountsQ.error ?? assetsQ.error ?? liabilitiesQ.error;
  if (queryError) {
    return (
      <ErrorState
        error={queryError}
        screen="accounts"
        metadata={{ failedQueries: 'accounts.list,assets.list,liabilities.list' }}
        onRetry={refetchAll}
      />
    );
  }

  const isDemo = householdQ.data?.isDemo ?? false;
  const accounts = accountsQ.data ?? [];
  const assets = assetsQ.data ?? [];
  const liabilities = liabilitiesQ.data ?? [];
  const connections = connectionsQ.data ?? [];
  const selectedAsset = selectedAssetId
    ? assets.find((asset) => asset.id === selectedAssetId) ?? null
    : null;
  const reviewSheetAccounts = accounts.filter((account) =>
    reviewSheetAccountIds.includes(account.id),
  );

  const openTaxSubtypeReview = (accountIds: string[]) => {
    setReviewSheetAccountIds(accountIds);
  };

  const closeTaxSubtypeReview = () => {
    setReviewSheetAccountIds([]);
  };

  const openFinancialStatement = () => {
    router.push('/(app)/financial-statement');
  };

  const handleTaxSubtypeSaved = async () => {
    await refetchAll();
    setReviewSheetAccountIds([]);
  };

  const groupedAccounts = {
    depository: accounts
      .filter((account) => account.type === 'depository')
      .sort(sortByBalanceDesc),
    investment: accounts
      .filter((account) => account.type === 'investment')
      .sort(sortByBalanceDesc),
    other: accounts.filter((account) => account.type === 'other').sort(sortByBalanceDesc),
  };
  const debtAccounts = accounts.filter(
    (account) => account.type === 'loan' || account.type === 'credit',
  ).sort(sortByBalanceDesc);
  const visibleInvestmentAccounts = showZeroBalanceInvestments
    ? groupedAccounts.investment
    : groupedAccounts.investment.filter((account) => Number(account.balance) !== 0);
  const hiddenZeroBalanceInvestmentCount =
    groupedAccounts.investment.length - visibleInvestmentAccounts.length;
  const liabilitiesByAccountId = new Map(
    liabilities.map((liability) => [liability.accountId, liability] as const),
  );

  const manualLiabilityEntries = assets.flatMap((asset) =>
    asset.manualLiabilities.map((manualLiability, index) => ({
      id: `${asset.id}-manual-${index}`,
      ...manualLiability,
    })),
  );
  const taxRefundOrOwe = taxEstimateQ.data?.result.refundOrOwe ?? 0;
  const balanceSheetSnapshot = buildBalanceSheetSnapshot({
    accounts,
    assets,
    liabilities,
    taxRefundOrOwe,
  });
  const getStatementSectionTotal = (key: string) =>
    balanceSheetSnapshot.assetSections.find((section) => section.key === key)?.total ?? 0;
  const depositoryTotal = getStatementSectionTotal('cash');
  const investmentTotal = getStatementSectionTotal('investments');
  const otherAccountTotal = groupedAccounts.other
    .filter((account) => !account.excludeFromNetWorthAndFire)
    .reduce((sum, account) => sum + Number(account.balance), 0);
  const trackedAssetValue = getStatementSectionTotal('property');
  const totalDebt = debtAccounts
    .filter((account) => !account.excludeFromNetWorthAndFire)
    .reduce((sum, account) => sum + Number(account.balance), 0)
    + manualLiabilityEntries.reduce(
      (sum, manualLiability) => sum + manualLiability.currentBalance,
      0,
    );
  const balanceSheetNetWorth = balanceSheetSnapshot.netWorth;
  const portfolioDayChangeAmount = holdingsQ.data?.summary?.dayChangeAmount ?? null;
  const portfolioDayChangePercent =
    portfolioDayChangeAmount != null && balanceSheetNetWorth !== 0
      ? portfolioDayChangeAmount / (balanceSheetNetWorth - portfolioDayChangeAmount)
      : null;
  const hasAnyBalanceSheetData =
    accounts.length > 0 || assets.length > 0 || manualLiabilityEntries.length > 0;
  const hasAccounts = accounts.length > 0;

  const renderManualBalanceEditor = (accountId: string) => {
    if (editingManualBalanceId !== accountId) return null;
    return (
      <View className="mt-2 rounded-xl bg-gray-50 dark:bg-gray-800/50 px-3 py-2 gap-2">
        <CurrencyInput
          label="New balance"
          value={manualBalanceDraft}
          onChangeText={setManualBalanceDraft}
          placeholder="0.00"
          size="sm"
        />
        <View className="flex-row gap-2">
          <Button
            size="sm"
            className="flex-1"
            onPress={handleSaveManualBalance}
            loading={updateAccountMutation.isPending}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="secondary"
            className="flex-1"
            onPress={cancelEditingManualBalance}
            disabled={updateAccountMutation.isPending}
          >
            Cancel
          </Button>
        </View>
      </View>
    );
  };

  const renderManualTimestamp = (balanceUpdatedAt: Date | string | null) => {
    const stale = isStaleManualBalance(balanceUpdatedAt);
    const label = balanceUpdatedAt
      ? `Updated ${formatRelativeTime(balanceUpdatedAt)}`
      : 'Balance not set';
    return (
      <Text
        className={`text-xs ${stale ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400'}`}
      >
        {label}
      </Text>
    );
  };

  const isStaleManualBalance = (balanceUpdatedAt: Date | string | null): boolean => {
    if (!balanceUpdatedAt) return true;
    const updated = balanceUpdatedAt instanceof Date
      ? balanceUpdatedAt.getTime()
      : new Date(balanceUpdatedAt).getTime();
    return Date.now() - updated > 30 * 24 * 60 * 60 * 1000;
  };

  return (
    <>
      <WebHead><title>Balance Sheet – Per Diem</title></WebHead>
      <AccountTaxSubtypeReviewSheet
        visible={reviewSheetAccounts.length > 0}
        accounts={reviewSheetAccounts}
        onClose={closeTaxSubtypeReview}
        onSaved={handleTaxSubtypeSaved}
      />
      <ScrollView
        className="flex-1 bg-canvas dark:bg-canvas-dark"
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={refetchAll} tintColor={c.primary} />
        }
      >
        <ConnectionErrorBanner onReconnected={refetchAll} />

        <PageContainer
          className={isDesktop ? '' : 'p-4'}
          size="wide"
        >
        <PageHeader
          title="Balance Sheet"
          subtitle="Accounts, assets, and liabilities"
          action={
            Platform.OS === 'web' ? (
              <Button size="sm" variant="secondary" onPress={openFinancialStatement}>
                View statement
              </Button>
            ) : undefined
          }
        />
        {Platform.OS === 'web' && !isDesktop && (
          <View className="mb-3 items-end">
            <Button size="sm" variant="secondary" onPress={openFinancialStatement}>
              View statement
            </Button>
          </View>
        )}
        <View className="gap-4">
          <View className="rounded-[18px] overflow-hidden shadow-card">
            <LinearGradient
              colors={['#173D2A', '#1F5237']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={{ padding: 28 }}
            >
              <View
                pointerEvents="none"
                style={{
                  position: 'absolute',
                  top: -50,
                  right: -50,
                  width: 220,
                  height: 220,
                  borderRadius: 110,
                  backgroundColor: 'rgba(255,255,255,0.03)',
                }}
              />
              <Text
                className="text-[10.5px] font-semibold uppercase tracking-[0.1em]"
                style={{ color: 'rgba(255,255,255,0.45)' }}
              >
                Net Worth
              </Text>
              <Money
                weight={500}
                className="text-[48px] text-white leading-[1.1] mt-2.5"
                style={{ letterSpacing: -1.5 }}
              >
                {formatCurrency(balanceSheetNetWorth, { decimals: 0 })}
              </Money>
              {portfolioDayChangeAmount != null && (
                <Money
                  weight={500}
                  className="text-[13px] mt-2"
                  style={{
                    color: portfolioDayChangeAmount < 0
                      ? 'rgba(255,160,140,0.9)'
                      : 'rgba(255,255,255,0.6)',
                  }}
                >
                  {formatCurrencyChange(portfolioDayChangeAmount)}
                  {portfolioDayChangePercent != null
                    ? ` (${portfolioDayChangePercent >= 0 ? '+' : ''}${formatPercent(portfolioDayChangePercent, 2)})`
                    : ''}{' '}
                  today
                </Money>
              )}
              <View className="flex-row gap-7 mt-5 flex-wrap">
                <View>
                  <Text className="text-[10.5px] uppercase tracking-[0.08em]" style={{ color: 'rgba(255,255,255,0.45)' }}>Cash</Text>
                  <Money weight={500} className="text-[15px] text-white mt-1">
                    {formatCurrency(depositoryTotal, { compact: true })}
                  </Money>
                </View>
                <View>
                  <Text className="text-[10.5px] uppercase tracking-[0.08em]" style={{ color: 'rgba(255,255,255,0.45)' }}>Investments</Text>
                  <Money weight={500} className="text-[15px] text-white mt-1">
                    {formatCurrency(investmentTotal, { compact: true })}
                  </Money>
                </View>
                <View>
                  <Text className="text-[10.5px] uppercase tracking-[0.08em]" style={{ color: 'rgba(255,255,255,0.45)' }}>Debt</Text>
                  <Money weight={500} className="text-[15px] text-white mt-1">
                    {formatCurrency(totalDebt, { compact: true })}
                  </Money>
                </View>
                {trackedAssetValue > 0 && (
                  <View>
                    <Text className="text-[10.5px] uppercase tracking-[0.08em]" style={{ color: 'rgba(255,255,255,0.45)' }}>Assets</Text>
                    <Money weight={500} className="text-[15px] text-white mt-1">
                      {formatCurrency(trackedAssetValue, { compact: true })}
                    </Money>
                  </View>
                )}
                {taxRefundOrOwe !== 0 && (
                  <View>
                    <Text className="text-[10.5px] uppercase tracking-[0.08em]" style={{ color: 'rgba(255,255,255,0.45)' }}>
                      {taxRefundOrOwe > 0 ? 'Tax refund' : 'Tax owed'}
                    </Text>
                    <Money weight={500} className="text-[15px] text-white mt-1">
                      {formatCurrency(Math.abs(taxRefundOrOwe), { compact: true })}
                    </Money>
                  </View>
                )}
              </View>
              <Text className="text-[12px] mt-4" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Includes linked accounts plus any homes, vehicles, and other assets you have added.
              </Text>
            </LinearGradient>
          </View>

          {isDemo && (
            <Text className="text-sm text-amber-700">
              Demo accounts are read-only. Account linking and sync actions are disabled.
            </Text>
          )}

          {hasAccounts && (
            <View className="flex-row items-center justify-between px-1">
              <Text className="text-xs font-semibold uppercase text-gray-400 dark:text-gray-500">
                Accounts
              </Text>
              <Pressable
                onPress={() => router.push('/(app)/integrations')}
                className="flex-row items-center gap-1 active:opacity-70"
                accessibilityRole="button"
                accessibilityLabel="Manage account integrations"
              >
                <Text className="text-sm font-medium text-forest-600 dark:text-forest-500">
                  Connect or manage accounts
                </Text>
                <Ionicons name="chevron-forward" size={14} color={c.primary} />
              </Pressable>
            </View>
          )}

          {groupedAccounts.depository.length > 0 && (
            <Card>
              <SectionHeader
                title={TYPE_LABELS.depository}
                action={
                  <Text className="text-xs text-gray-400">
                    {formatCurrency(depositoryTotal, { compact: true })}
                  </Text>
                }
              />
              {groupedAccounts.depository.map((account, index) => {
                const connection = connections.find((item) => item.id === account.connectionId);
                const isLast = index === groupedAccounts.depository.length - 1;
                const isManual = account.provider === 'manual';
                return (
                  <View
                    key={account.id}
                    className={`py-3 ${isLast ? '' : 'border-b border-gray-50'}`}
                  >
                    <Pressable
                      onPress={() =>
                        isManual
                          ? startEditingManualBalance(account)
                          : router.push({
                              pathname: '/(app)/transactions',
                              params: { accountId: account.id, accountName: account.name },
                            })
                      }
                      className="flex-row items-center gap-3 active:opacity-70"
                    >
                      <InstitutionLogo
                        logoBase64={account.institutionLogoBase64}
                        name={account.institutionName ?? account.name}
                        size={32}
                      />
                      <View className="flex-1">
                        <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">{account.name}</Text>
                        <View className="flex-row flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                          {account.mask && (
                            <Text className="text-xs text-gray-400">••••{account.mask}</Text>
                          )}
                          {connection && (
                            <Text className="text-xs text-gray-400">{connection.institutionName}</Text>
                          )}
                          {isManual && renderManualTimestamp(account.balanceUpdatedAt)}
                        </View>
                      </View>
                      <View className="flex-row items-center gap-2">
                        <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                          {formatCurrency(Number(account.balance))}
                        </Text>
                        {isManual ? (
                          <View style={{ width: 14 }} />
                        ) : (
                          <Ionicons name="chevron-forward" size={14} color="#9ca3af" />
                        )}
                      </View>
                    </Pressable>
                    {isManual && renderManualBalanceEditor(account.id)}
                  </View>
                );
              })}
            </Card>
          )}

          {groupedAccounts.investment.length > 0 && (
            <Card>
              <SectionHeader
                title={TYPE_LABELS.investment}
                action={
                  <View className="items-end">
                    <Text className="text-xs text-gray-400">
                      {formatCurrency(investmentTotal, { compact: true })}
                    </Text>
                    <Pressable onPress={() => router.push('/(app)/holdings')}>
                      <Text className="mt-1 text-xs font-medium text-green-600">Open portfolio</Text>
                    </Pressable>
                  </View>
                }
              />
              {visibleInvestmentAccounts.map((account, index) => {
                const connection = connections.find((item) => item.id === account.connectionId);
                const isLast = index === visibleInvestmentAccounts.length - 1;
                const isManual = account.provider === 'manual';
                return (
                  <View
                    key={account.id}
                    className={`py-3 ${isLast ? '' : 'border-b border-gray-50'}`}
                  >
                    <Pressable
                      onPress={() =>
                        isManual ? startEditingManualBalance(account) : router.push('/(app)/holdings')
                      }
                      className="flex-row items-center gap-3 active:opacity-70"
                    >
                      <InstitutionLogo
                        logoBase64={account.institutionLogoBase64}
                        name={account.institutionName ?? account.name}
                        size={32}
                      />
                      <View className="flex-1">
                        <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">{account.name}</Text>
                        <View className="flex-row flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                          {account.mask && (
                            <Text className="text-xs text-gray-400">••••{account.mask}</Text>
                          )}
                          {connection && (
                            <Text className="text-xs text-gray-400">{connection.institutionName}</Text>
                          )}
                          {account.subtype && (
                            <Text className="text-xs text-gray-300">
                              {getAccountTaxSubtypeLabel(account.subtype) ?? toTitleCase(account.subtype)}
                            </Text>
                          )}
                          {isManual && renderManualTimestamp(account.balanceUpdatedAt)}
                          {account.excludeFromNetWorthAndFire && (
                            <Text className="text-xs text-amber-600 dark:text-amber-400">
                              Excluded from net worth & FIRE
                            </Text>
                          )}
                        </View>
                      </View>
                      <View className="flex-row items-center gap-2">
                        <AccountPlanningButton
                          onPress={() => openTaxSubtypeReview([account.id])}
                        />
                        {account.needsTaxSubtypeReview && (
                          <ReviewHintIcon
                            tooltip="Account type needs review"
                            onPress={() => openTaxSubtypeReview([account.id])}
                          />
                        )}
                        <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                          {formatCurrency(Number(account.balance))}
                        </Text>
                        {isManual ? (
                          <View style={{ width: 14 }} />
                        ) : (
                          <Ionicons name="chevron-forward" size={14} color="#9ca3af" />
                        )}
                      </View>
                    </Pressable>
                    {isManual && renderManualBalanceEditor(account.id)}
                  </View>
                );
              })}
              {hiddenZeroBalanceInvestmentCount > 0 && (
                <Pressable
                  onPress={() => setShowZeroBalanceInvestments((current) => !current)}
                  className={`${visibleInvestmentAccounts.length > 0 ? 'border-t border-gray-50 dark:border-gray-700 pt-3' : 'pt-1'} active:opacity-70`}
                >
                  <Text className="text-sm font-medium text-forest-600 dark:text-forest-500">
                    {showZeroBalanceInvestments
                      ? 'Hide accounts with $0 balance'
                      : `Show account${hiddenZeroBalanceInvestmentCount === 1 ? '' : 's'} with $0 balance`}
                  </Text>
                </Pressable>
              )}
            </Card>
          )}

          {(debtAccounts.length > 0 || manualLiabilityEntries.length > 0) && (
            <Card>
              <SectionHeader
                title="Loans"
                action={
                  <Text className="text-xs text-gray-400">
                    {formatCurrency(totalDebt, { compact: true })}
                  </Text>
                }
              />
              {debtAccounts.map((account) => {
                const liability = liabilitiesByAccountId.get(account.id);
                const badgeLabel =
                  liability?.liabilityType === 'mortgage'
                    ? 'Mortgage'
                    : liability?.liabilityType === 'student'
                      ? 'Student loan'
                      : account.type === 'credit'
                        ? 'Credit card'
                        : 'Loan';

                const isMortgage = liability?.liabilityType === 'mortgage';
                const isEditingThisLiability = !!(liability && editingLiabilityId === liability.id);
                const mortgageMissingDetails =
                  isMortgage &&
                  !((liability?.principalAndInterestMonthly ?? 0) > 0) &&
                  !((liability?.propertyTaxMonthly ?? 0) > 0) &&
                  !((liability?.homeInsuranceMonthly ?? 0) > 0) &&
                  !((liability?.hoaMonthly ?? 0) > 0);
                const rowPressable = isMortgage && !isEditingThisLiability && liability;

                return (
                  <Pressable
                    key={account.id}
                    onPress={rowPressable ? () => startEditingLiability(liability) : undefined}
                    disabled={!rowPressable}
                    className="flex-row items-start justify-between gap-3 py-3 border-b border-gray-50 dark:border-gray-700"
                  >
                    <InstitutionLogo
                      logoBase64={account.institutionLogoBase64}
                      name={account.institutionName ?? liability?.institutionName ?? account.name}
                      size={32}
                    />
                    <View className="flex-1">
                      <View className="flex-row items-center gap-2">
                        <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">{account.name}</Text>
                        <Badge
                          label={badgeLabel}
                          variant={liability?.liabilityType === 'mortgage' ? 'blue' : liability?.liabilityType === 'student' ? 'yellow' : 'gray'}
                        />
                      </View>
                      <Text className="text-xs text-gray-500 mt-1">
                        {account.institutionName ?? liability?.institutionName ?? 'Linked account'}
                      </Text>
                      <Text className="text-xs text-gray-400 mt-1">
                        {[
                          liability?.interestRatePercentage != null
                            ? `${liability.interestRatePercentage.toFixed(2)}% APR`
                            : null,
                          liability?.nextPaymentAmount != null
                            ? `${formatCurrency(liability.nextPaymentAmount, { decimals: 0 })}/mo`
                            : null,
                          account.mask ? `••••${account.mask}` : null,
                        ]
                          .filter(Boolean)
                          .join(' · ') || toTitleCase(account.subtype ?? account.type)}
                      </Text>
                      {isMortgage && liability && (
                        <View className="mt-2">
                          {!isEditingThisLiability && (
                            mortgageMissingDetails ? (
                              <View className="flex-row items-center gap-1.5">
                                <Ionicons name="warning-outline" size={12} color="#d97706" />
                                <Text className="text-xs text-amber-700 dark:text-amber-400 flex-1" numberOfLines={2}>
                                  Add P&I, property tax, and insurance to improve FIRE planning
                                </Text>
                              </View>
                            ) : (
                              <Text className="text-xs text-gray-400" numberOfLines={1}>
                                P&I {formatCurrency(liability.principalAndInterestMonthly ?? 0, { decimals: 0 })}/mo
                                {(liability.propertyTaxMonthly ?? 0) > 0 && ` · Tax ${formatCurrency(liability.propertyTaxMonthly ?? 0, { decimals: 0 })}/mo`}
                                {(liability.homeInsuranceMonthly ?? 0) > 0 && ` · Ins ${formatCurrency(liability.homeInsuranceMonthly ?? 0, { decimals: 0 })}/mo`}
                                {(liability.hoaMonthly ?? 0) > 0 && ` · HOA ${formatCurrency(liability.hoaMonthly ?? 0, { decimals: 0 })}/mo`}
                              </Text>
                            )
                          )}

                          {editingLiabilityId === liability.id && liabilityDraft && (
                            <View className="mt-2 gap-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3">
                              <Text className="text-xs text-gray-500 dark:text-gray-400">
                                Split this payment into fixed principal and interest plus escrow items that continue after payoff.
                              </Text>
                              <CurrencyInput
                                label="Principal & interest monthly"
                                value={liabilityDraft.principalAndInterestMonthly}
                                onChangeText={(value) =>
                                  setLiabilityDraft((current) =>
                                    current ? { ...current, principalAndInterestMonthly: value } : current,
                                  )
                                }
                                placeholder="0.00"
                                size="sm"
                              />
                              <CurrencyInput
                                label="Property tax monthly"
                                value={liabilityDraft.propertyTaxMonthly}
                                onChangeText={(value) =>
                                  setLiabilityDraft((current) =>
                                    current ? { ...current, propertyTaxMonthly: value } : current,
                                  )
                                }
                                placeholder="0.00"
                                size="sm"
                              />
                              <CurrencyInput
                                label="Home insurance monthly"
                                value={liabilityDraft.homeInsuranceMonthly}
                                onChangeText={(value) =>
                                  setLiabilityDraft((current) =>
                                    current ? { ...current, homeInsuranceMonthly: value } : current,
                                  )
                                }
                                placeholder="0.00"
                                size="sm"
                              />
                              <CurrencyInput
                                label="HOA monthly"
                                value={liabilityDraft.hoaMonthly}
                                onChangeText={(value) =>
                                  setLiabilityDraft((current) =>
                                    current ? { ...current, hoaMonthly: value } : current,
                                  )
                                }
                                placeholder="0.00"
                                size="sm"
                              />
                              <View className="flex-row gap-2">
                                <Button
                                  size="sm"
                                  className="flex-1"
                                  onPress={handleSaveLiabilityPlanning}
                                  loading={updateLiabilityPlanningMutation.isPending}
                                >
                                  Save Breakdown
                                </Button>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  className="flex-1"
                                  onPress={cancelEditingLiability}
                                  disabled={updateLiabilityPlanningMutation.isPending}
                                >
                                  Cancel
                                </Button>
                              </View>
                            </View>
                          )}
                        </View>
                      )}
                    </View>
                    <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                      {formatCurrency(Number(account.balance))}
                    </Text>
                  </Pressable>
                );
              })}
              {manualLiabilityEntries.map((manualLiability, index) => (
                <View
                  key={manualLiability.id}
                  className={`flex-row items-start justify-between gap-3 py-3 ${
                    index === manualLiabilityEntries.length - 1 ? '' : 'border-b border-gray-50'
                  }`}
                >
                  <View className="flex-1">
                    <View className="flex-row items-center gap-2">
                      <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">Manual loan</Text>
                      <Badge label="Manual loan" variant="gray" />
                    </View>
                    <Text className="text-xs text-gray-500 mt-1">Manually tracked liability</Text>
                    <Text className="text-xs text-gray-400 mt-1">
                      {manualLiability.interestRatePercentage.toFixed(2)}% APR · {formatCurrency(manualLiability.monthlyPayment, { decimals: 0 })}/mo
                    </Text>
                  </View>
                  <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    {formatCurrency(manualLiability.currentBalance, { decimals: 0 })}
                  </Text>
                </View>
              ))}
            </Card>
          )}

          {!isDemo && (
            <Card>
              <SectionHeader
                title="Assets"
                action={
                  <View className="flex-row items-center gap-3">
                    {assets.length > 0 && (
                      <Text className="text-xs text-gray-400">
                        {assets.length} item{assets.length === 1 ? '' : 's'} · {formatCurrency(trackedAssetValue, { compact: true })}
                      </Text>
                    )}
                    {!addingAsset && (
                      <Pressable onPress={() => setAddingAsset(true)} className="p-1">
                        <Ionicons name="add" size={20} color={c.primary} />
                      </Pressable>
                    )}
                  </View>
                }
              />
              {addingAsset && (
                <View className="mb-4 pb-4 border-b border-gray-100 dark:border-gray-700">
                  <AssetForm
                    open={addingAsset}
                    onClose={() => setAddingAsset(false)}
                    onEstimateValue={async (input) => {
                      return await estimateValueMutation.mutateAsync(input);
                    }}
                    onSave={async (input) => {
                      await createAssetMutation.mutateAsync(input);
                      await assetsQ.refetch();
                      setAddingAsset(false);
                    }}
                    saving={createAssetMutation.isPending}
                    estimating={estimateValueMutation.isPending}
                  />
                </View>
              )}
              {assets.length === 0 && !addingAsset && (
                <Text className="text-sm text-gray-400 text-center py-3">
                  No assets added yet. Add homes, vehicles, or other major assets.
                </Text>
              )}
              <View className="flex-row flex-wrap gap-3">
                {assets.map((asset) => {
                  const colors = getAssetIconColors(asset.assetType);
                  const isSelected = selectedAsset?.id === asset.id;
                  return (
                    <Pressable
                      key={asset.id}
                      onPress={() => setSelectedAssetId((current) => current === asset.id ? null : asset.id)}
                      className={`w-[104px] rounded-2xl border px-3 py-4 ${
                        isSelected ? 'border-forest-500 bg-green-50 dark:bg-green-900/30' : 'border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800'
                      }`}
                    >
                      <View className={`w-14 h-14 rounded-2xl items-center justify-center ${colors.bg}`}>
                        <Ionicons name={getAssetIconName(asset.assetType)} size={28} color={colors.fg} />
                      </View>
                      <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200 mt-3" numberOfLines={2}>
                        {asset.name}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>

              {selectedAsset && (
                <View className="mt-4 rounded-2xl bg-gray-50 dark:bg-gray-800 px-4 py-4">
                  <View className="flex-row items-start justify-between gap-3">
                    <View className="flex-1">
                      <Text className="text-base font-semibold text-gray-900 dark:text-gray-100">{selectedAsset.name}</Text>
                    <Text className="text-sm text-gray-500 mt-1">{formatAssetMeta(selectedAsset)}</Text>
                    </View>
                    <Badge label={toTitleCase(selectedAsset.assetType)} variant="gray" />
                  </View>

                  <View className="flex-row gap-6 mt-4 flex-wrap">
                    <View>
                      <Text className="text-xs uppercase tracking-wide text-gray-400">Value</Text>
                      <Text className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {selectedAsset.estimatedValue != null
                          ? formatCurrency(selectedAsset.estimatedValue, { decimals: 0 })
                          : 'Missing'}
                      </Text>
                    </View>
                    <View>
                      <Text className="text-xs uppercase tracking-wide text-gray-400">Growth</Text>
                      <Text className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {(selectedAsset.annualGrowthRate * 100).toFixed(1)}% / yr
                      </Text>
                    </View>
                  </View>

                  <Pressable
                    onPress={async () => {
                      const confirmed = await confirmDestructive({
                        title: 'Remove Asset',
                        message: `Remove "${selectedAsset.name}" from your balance sheet?`,
                      });
                      if (!confirmed) return;

                      await deleteAssetMutation.mutateAsync({ id: selectedAsset.id });
                      setSelectedAssetId(null);
                      await assetsQ.refetch();
                    }}
                    className="mt-4"
                  >
                    <Text className="text-xs text-red-400">Remove asset</Text>
                  </Pressable>
                </View>
              )}
            </Card>
          )}

          {groupedAccounts.other.length > 0 && (
            <Card>
              <SectionHeader
                title={TYPE_LABELS.other}
                action={
                  <Text className="text-xs text-gray-400">
                    {formatCurrency(otherAccountTotal, { compact: true })}
                  </Text>
                }
              />
              {groupedAccounts.other.map((account, index) => {
                const connection = connections.find((item) => item.id === account.connectionId);
                const isLast = index === groupedAccounts.other.length - 1;
                const isManual = account.provider === 'manual';
                return (
                  <View
                    key={account.id}
                    className={`py-3 ${isLast ? '' : 'border-b border-gray-50'}`}
                  >
                    <Pressable
                      onPress={() =>
                        isManual
                          ? startEditingManualBalance(account)
                          : router.push({
                              pathname: '/(app)/transactions',
                              params: { accountId: account.id, accountName: account.name },
                            })
                      }
                      className="flex-row items-center gap-3 active:opacity-70"
                    >
                      <InstitutionLogo
                        logoBase64={account.institutionLogoBase64}
                        name={account.institutionName ?? account.name}
                        size={32}
                      />
                      <View className="flex-1">
                        <Text className="text-sm font-medium text-gray-800 dark:text-gray-200">{account.name}</Text>
                        <View className="flex-row flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                          {connection && (
                            <Text className="text-xs text-gray-400">{connection.institutionName}</Text>
                          )}
                          {account.subtype && (
                            <Text className="text-xs text-gray-300">
                              {getAccountTaxSubtypeLabel(account.subtype) ?? toTitleCase(account.subtype)}
                            </Text>
                          )}
                          {isManual && renderManualTimestamp(account.balanceUpdatedAt)}
                          {account.excludeFromNetWorthAndFire && (
                            <Text className="text-xs text-amber-600 dark:text-amber-400">
                              Excluded from net worth & FIRE
                            </Text>
                          )}
                        </View>
                      </View>
                      <View className="flex-row items-center gap-2">
                        {account.needsTaxSubtypeReview && (
                          <ReviewHintIcon
                            tooltip="Account type needs review"
                            onPress={() => openTaxSubtypeReview([account.id])}
                          />
                        )}
                        <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                          {formatCurrency(Number(account.balance))}
                        </Text>
                        {isManual ? (
                          <View style={{ width: 14 }} />
                        ) : (
                          <Ionicons name="chevron-forward" size={14} color="#9ca3af" />
                        )}
                      </View>
                    </Pressable>
                    {isManual && renderManualBalanceEditor(account.id)}
                  </View>
                );
              })}
            </Card>
          )}

          {!hasAnyBalanceSheetData && (
            <EmptyState
              title="No balance sheet data yet"
              message="Manage integrations to connect accounts, or add major assets to see your full financial picture."
              actionLabel="Manage integrations"
              onAction={() => router.push('/(app)/integrations')}
            />
          )}

          {taxEstimateQ.data && (
            <Card>
              <SectionHeader
                title="Tax Estimate"
                action={
                  <Pressable onPress={() => router.push('/(app)/tax-dashboard')}>
                    <Text className="text-sm font-medium text-forest-600 dark:text-forest-500">
                      Open tax dashboard
                    </Text>
                  </Pressable>
                }
              />
              <Text className="text-xs text-gray-500 dark:text-gray-400">
                {taxEstimateQ.data.tax.effectiveRate > 0
                  ? `${formatPercent(taxEstimateQ.data.tax.effectiveRate, 1)} effective rate · ${formatCurrency(taxEstimateQ.data.tax.total, { compact: true, decimals: 0 })} projected`
                  : 'Projected for the current tax year'}
              </Text>
              <Text
                className={`mt-2 text-base font-semibold ${
                  taxEstimateQ.data.result.refundOrOwe >= 0 ? 'text-green-600 dark:text-green-500' : 'text-red-600 dark:text-red-400'
                }`}
              >
                {taxEstimateQ.data.result.refundOrOwe >= 0 ? 'Refund ' : 'Owe '}
                {formatCurrency(Math.abs(taxEstimateQ.data.result.refundOrOwe), { decimals: 0 })}
              </Text>
            </Card>
          )}

        </View>
        </PageContainer>
      </ScrollView>
    </>
  );
}
